import {
  Autocomplete,
  Avatar,
  Box,
  createFilterOptions,
  ListItemAvatar,
  TextField,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Label from 'src/components/Label';
import { SupportUserIcon } from 'src/components/SupportUserIcon';
import useAuth from 'src/hooks/useAuth';
import { Workspace } from 'src/models/workspace.interface';
import { feature } from 'src/models/workspaceUserFeatures.interface';

type Option = Pick<Workspace, '_id' | 'name' | 'images' | 'description'> & {
  type: string;
};

function WorkspaceSearch() {
  const { enqueueSnackbar } = useSnackbar();

  const { switchWorkspace, allSpaces, user } = useAuth();

  const sortedSpaces = [...allSpaces].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const handleSpaceChange = async (workspaceId): Promise<void> => {
    try {
      if (workspaceId) {
        await switchWorkspace(workspaceId);
      }
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error'
      });
    }
  };

  const filterOptions = createFilterOptions({
    stringify: (option: Option) => `${option.name} ${option.description}`
  });

  return (
    <Autocomplete
      id="search-autocomplete"
      openOnFocus
      options={sortedSpaces}
      value={user.selectedSpace as unknown as (typeof sortedSpaces)[number]}
      blurOnSelect
      autoHighlight
      getOptionLabel={(item) => item.name ?? ''}
      filterOptions={filterOptions}
      onChange={(_, data: Option) => {
        handleSpaceChange(data?._id);
      }}
      isOptionEqualToValue={(option, value) =>
        option._id === user.selectedSpace
      }
      ListboxProps={{ style: { maxHeight: '35rem' } }}
      renderOption={(props, option) => (
        <li {...props} key={option._id} style={{ maxHeight: '30rem' }}>
          <Box display="flex" p={1}>
            <Box key={option._id} sx={{ display: 'flex' }}>
              <ListItemAvatar>
                <Avatar alt={option.name} src={option?.images?.[0]?.imageUrl} />
              </ListItemAvatar>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    key={option._id}
                    p={0}
                    mr={1}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {option.name}
                  </Typography>
                  <Label
                    color={option.type === 'workspace' ? 'info' : 'success'}
                  >
                    {option.type}
                  </Label>
                </Box>
                <Typography variant="caption">{option.description}</Typography>
              </Box>
            </Box>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <Box display="flex">
          <TextField
            {...params}
            size="small"
            variant="outlined"
            placeholder={'Search workspaces and organisations'}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <Box display="flex" alignItems="center">
                  <SupportUserIcon
                    features={[feature.support.supportAdmin]}
                    fontSize="small"
                  />
                </Box>
              )
            }}
          />
        </Box>
      )}
      sx={{
        maxWidth: '330px'
      }}
      fullWidth
    />
  );
}

export default WorkspaceSearch;
